const CACHE_KEY = 'apiCache';

let cache = JSON.parse(localStorage.getItem(CACHE_KEY)) || {
  ratingsResponse: null,
  gameResponse: null,
  lastUpdated: null
};
let useCache = true;

const saveCache = () => localStorage.setItem(CACHE_KEY, JSON.stringify(cache));

export const fetchWithCache = async (key, url) => {
  if (useCache && cache[key]) {
    console.log(`✅ Using cached ${key}`);
    return cache[key];
  }

  console.log(`🌐 Fetching ${key} from ${url}`);
  const response = await fetch(url);
  const data = await response.json();

  cache[key] = data;
  cache.lastUpdated = Date.now();
  saveCache();

  return data;
};

export const setUseCache = val => (useCache = val);

//export const getTeamLogoUrl = (id, light = true) => `https://cdn.collegefootballdata.com/logos${light ? "" : "-dark"}/48/${id}.png`;
export const getTeamLogoUrl = (id, light = true) => `https://a.espncdn.com/combiner/i?img=/i/teamlogos/ncaa/500${light ? "" : "-dark"}/${id}.png&w=50&h=50`;

export const getTeamLogoUrl2 = (id, name, logos, logo_type) => {


    const logo = (logos[name] && logos[name][logo_type] && logos[name]?.[logo_type] != "");

    return logo ? logos[name]?.[logo_type] : getTeamLogoUrl(id, true);
}
export const getTeamLogoUrl3 = (id, light = true) => `https://a.espncdn.com/combiner/i?img=/i/teamlogos/ncaa/500${light ? "" : "-dark"}/${id}.png`;

export const applyHeadToHeadTiebreakers = (teams, grades = {}, threshold = 0.001, year = 2026) => {
    // 1. Apply fallback chain for team values: passed grades -> team.value -> team.avg -> 0
    const processedTeams = teams.map(team => ({
        ...team,
        value: grades?.[team.team] ?? team.value ?? team.avg ?? 0
    }));

    const HEAD_TO_HEAD_GAP = 10;
    const INDEX_WINDOW = year === 2026 ? 10 : 3;
    const MAX_PASSES = year === 2026 ? 10 : 3;

    // Recent games matter slightly more without overriding overall team value
    const RECENCY_WEIGHT = 1;

    function getGameDate(game) {
        if (!game?.date) return null;
        const date = new Date(game.date);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    function getRecencyScore(game) {
        const gameDate = getGameDate(game);
        if (!gameDate) return 0;

        const seasonStart = new Date(`${year}-01-01`);
        const seasonEnd = new Date(`${year}-12-31`);
        const seasonLength = seasonEnd - seasonStart;
        const age = Math.max(0, seasonEnd - gameDate);

        // 1 = very recent, 0 = very old
        return Math.max(0, 1 - age / seasonLength);
    }

    function findHeadToHeadWinner(teamA, teamB) {
        const aMatches = (teamA.LossesTeams || []).filter(entry =>
            (entry.loss === teamB.team || entry.fcs === teamB.team) &&
            (entry.completed === true || entry.game?.manual)
        ).map(game => ({
            winner: game.win ? teamA : teamB,
            loser: game.win ? teamB : teamA,
            game
        }));

        const bMatches = (teamB.LossesTeams || []).filter(entry =>
            (entry.loss === teamA.team || entry.fcs === teamA.team) &&
            (entry.completed === true || entry.game?.manual)
        ).map(game => ({
            winner: game.win ? teamB : teamA,
            loser: game.win ? teamA : teamB,
            game
        }));

        const allMeetings = [...aMatches, ...bMatches];
        if (allMeetings.length === 0) return null;

        // Most recent meeting decides the H2H edge (e.g., championship rematches overturn regular season losses)
        allMeetings.sort((x, y) => {
            const dx = x.game?.game?.week ?? x.game?.week ?? 0;
            const dy = y.game?.game?.week ?? y.game?.week ?? 0;
            return dy - dx; // newest first
        });

        return allMeetings;
    }

    function computeEdges(list) {
        const edges = new Map();
        const indegree = new Map();

        list.forEach(team => {
            edges.set(team.team, new Set());
            indegree.set(team.team, 0);
        });

        for (let i = 0; i < list.length; i++) {
            const teamA = list[i];

            for (
                let j = i + 1;
                j < Math.min(list.length, i + INDEX_WINDOW + 1);
                j++
            ) {
                const teamB = list[j];

                const valueGap = Math.abs(teamA.value - teamB.value);
                const h2hall = findHeadToHeadWinner(teamA, teamB);

                // Only apply H2H when teams are reasonably close based on threshold/gap scaling
                if (valueGap > (h2hall && h2hall.length > 1 ? 3 : 1) * HEAD_TO_HEAD_GAP / (j - i)) {
                    continue;
                }

                const h2h = h2hall ? h2hall[0] : null;
                if (!h2h) continue;

                const { winner, loser, game } = h2h;

                const lossesA = teamA.gp - teamA.wins;
                const lossesB = teamB.gp - teamB.wins;

                if (lossesB > lossesA + 1) {
                    continue;
                }

                const recency = getRecencyScore(game);
                const h2hStrength = 1 + recency * RECENCY_WEIGHT;

                edges.get(winner.team).add({
                    team: loser.team,
                    strength: h2hStrength
                });
            }
        }

        // Cycle detection and elimination
        const cycles = new Set();
        const visited = new Set();
        const stack = [];

        function dfs(node) {
            if (stack.includes(node)) {
                const index = stack.indexOf(node);
                if (stack.length - index >= 3) {
                    for (let i = index; i < stack.length; i++) {
                        cycles.add(stack[i]);
                    }
                }
                return;
            }

            if (visited.has(node)) return;

            visited.add(node);
            stack.push(node);

            for (const edge of edges.get(node) || []) {
                dfs(edge.team);
            }

            stack.pop();
        }

        for (const node of edges.keys()) {
            dfs(node);
        }

        edges.forEach((targets, from) => {
            if (cycles.has(from)) {
                targets.clear();
            } else {
                for (const edge of [...targets]) {
                    if (cycles.has(edge.team)) {
                        targets.delete(edge);
                    }
                }
            }
        });

        // Recompute indegrees
        indegree.clear();
        list.forEach(team => {
            indegree.set(team.team, 0);
        });

        edges.forEach(targets => {
            targets.forEach(edge => {
                indegree.set(
                    edge.team,
                    (indegree.get(edge.team) || 0) + 1
                );
            });
        });

        return { edges, indegree };
    }

    function stableTopo(list, edges, indegree) {
        const nameToIndex = new Map(
            list.map((team, index) => [team.team, index])
        );

        const zero = [];
        indegree.forEach((degree, name) => {
            if (degree === 0) {
                zero.push(name);
            }
        });

        zero.sort((a, b) => nameToIndex.get(a) - nameToIndex.get(b));

        const order = [];

        while (zero.length) {
            const node = zero.shift();
            order.push(node);

            for (const edge of edges.get(node) || []) {
                const target = edge.team;
                indegree.set(target, indegree.get(target) - 1);

                if (indegree.get(target) === 0) {
                    const insertAt = zero.findIndex(
                        name => nameToIndex.get(name) > nameToIndex.get(target)
                    );

                    if (insertAt === -1) {
                        zero.push(target);
                    } else {
                        zero.splice(insertAt, 0, target);
                    }
                }
            }
        }

        if (order.length < list.length) {
            const placed = new Set(order);
            list.forEach(team => {
                if (!placed.has(team.team)) {
                    order.push(team.team);
                }
            });
        }

        const byName = new Map(list.map(team => [team.team, team]));
        return order.map(name => byName.get(name));
    }

    let current = processedTeams.slice();

    for (let pass = 0; pass < MAX_PASSES; pass++) {
        const { edges, indegree } = computeEdges(current);
        const next = stableTopo(current, edges, indegree);

        const changed = next.some(
            (team, index) => team.team !== current[index].team
        );

        current = next;
        if (!changed) break;
    }

    return current;
};
export const getWins = (recordStr) => {
    if (!recordStr) return 0;
    return parseFloat(recordStr.split('-')[0]) || 0;
};